<template>
  <div
    class="w-full bg-white border border-[#E5E7EB] rounded-xl shadow-sm text-left overflow-hidden"
    data-testid="exceptionality-progress-timeline"
  >
    <button
      class="w-full flex items-center justify-between px-5 py-4"
      data-testid="progress-timeline-toggle-button"
      @click="isExpanded = !isExpanded"
    >
      <div class="flex items-center gap-3">
        <div
          v-if="headerStatus.type === 'in-progress'"
          class="w-8 h-8 rounded-full border-4 border-alert bg-[#FEF3E7] flex items-center justify-center shrink-0"
          data-testid="progress-timeline-status-icon"
        >
          <span class="text-textPrimary text-[11px] font-bold leading-none">
            {{ headerStatus.current }}/{{ headerStatus.total }}
          </span>
        </div>

        <div
          v-else-if="headerStatus.type === 'approved'"
          class="w-8 h-8 rounded-full border border-negative bg-[#FDF3F4] flex items-center justify-center shrink-0"
          data-testid="progress-timeline-status-icon"
        >
          <div class="w-3.5 h-3.5 rounded-full bg-negative flex items-center justify-center">
            <v-icon :size="10" color="white">{{ mdiCheck }}</v-icon>
          </div>
        </div>

        <div
          v-else
          class="w-8 h-8 rounded-full bg-negative flex items-center justify-center shrink-0"
          data-testid="progress-timeline-status-icon"
        >
          <v-icon :size="18" color="white">{{ mdiClose }}</v-icon>
        </div>

        <span class="text-[17px] font-bold text-textPrimary tracking-tight">{{ title }}</span>
      </div>
      <v-icon :size="22" color="#6B7280">
        {{ isExpanded ? mdiChevronUp : mdiChevronDown }}
      </v-icon>
    </button>

    <div v-show="isExpanded" class="relative px-5 pb-5">
      <div class="absolute left-[29px] top-0 bottom-4 w-[2px] bg-[#CBD5E1] z-0"></div>

      <template v-for="(layer, layerIdx) in layers" :key="layerIdx">
        <div class="flex gap-3 relative z-10 mb-2">
          <div class="w-5 shrink-0 flex justify-center pt-1">
            <div class="w-2 h-2 rounded-full bg-negative"></div>
          </div>
          <span
            class="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-normal tracking-wide border border-[#D1D5DB] text-textPrimary bg-white"
            :data-testid="`progress-layer-tag-${layerIdx}`"
          >
            {{ layer.operatorLabel }}
          </span>
        </div>

        <div
          v-for="(event, eventIdx) in layer.events"
          :key="`${layerIdx}-${eventIdx}`"
          class="flex gap-3 relative z-10 mb-2"
          :data-testid="`progress-event-${layerIdx}-${eventIdx}`"
        >
          <div class="w-5 shrink-0 flex justify-center pt-px">
            <div
              v-if="event.status === 'approved'"
              class="w-4 h-4 rounded-full bg-negative flex items-center justify-center"
            >
              <v-icon :size="10" color="white">{{ mdiCheck }}</v-icon>
            </div>
            <div
              v-else-if="event.status === 'pending'"
              class="w-3 h-3 rounded-full border-2 border-alert bg-white mt-1"
            ></div>
            <div
              v-else-if="event.status === 'rejected'"
              class="w-5 h-5 rounded-full bg-negative flex items-center justify-center"
            >
              <v-icon :size="14" color="white">{{ mdiClose }}</v-icon>
            </div>
            <div v-else class="w-2.5 h-2.5 rounded-full bg-neutral50 mt-1"></div>
          </div>

          <div class="flex flex-col min-w-0">
            <p class="text-[14px] text-neutral50 leading-snug">
              <span v-if="event.date" class="font-bold mr-1.5">{{ event.date }}</span>
              <span
                v-if="event.badge"
                class="inline-flex items-center px-1.5 py-px rounded text-[10px] font-bold mr-1.5 align-middle leading-tight border"
                :class="getBadgeClasses(event.badge.variant)"
              >
                {{ event.badge.text }}
              </span>
              <span class="font-normal">{{ event.description }}</span>
            </p>
            <p v-if="event.time" class="text-[12px] text-gray-400 mt-0.5 font-normal tracking-wide">
              {{ event.time }}
            </p>
            <div
              v-if="event.justification?.length"
              class="mt-1 text-[13px] text-textPrimary leading-snug whitespace-pre-line break-words"
              :data-testid="`progress-event-justification-${layerIdx}-${eventIdx}`"
            >
              <p v-for="(item, itemIdx) in event.justification" :key="itemIdx">
                <span v-if="item.title" class="font-bold">{{ item.title }}:</span>
                {{ item.text }}
              </p>
            </div>
          </div>
        </div>
      </template>

      <div class="flex gap-3 relative z-10" data-testid="progress-base-event">
        <div class="w-5 shrink-0 flex justify-center pt-1">
          <div class="w-2.5 h-2.5 rounded-full bg-primary20"></div>
        </div>
        <div class="flex flex-col">
          <p class="text-[14px] text-neutral50 leading-snug">
            <span class="font-bold mr-1.5">{{ baseEvent.date }}</span>
            <span class="font-normal">{{ baseEvent.description }}</span>
          </p>
          <p
            v-if="baseEvent.time"
            class="text-[12px] text-gray-400 mt-0.5 font-normal tracking-wide"
          >
            {{ baseEvent.time }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { mdiCheck, mdiClose, mdiChevronUp, mdiChevronDown } from '@mdi/js'

import type {
  ProgressHeaderStatus,
  ProgressLayer,
  ProgressBaseEvent
} from '../types/progress-timeline.types'

const props = withDefaults(
  defineProps<{
    headerStatus: ProgressHeaderStatus
    title: string
    layers: ProgressLayer[]
    baseEvent: ProgressBaseEvent
    defaultExpanded?: boolean
  }>(),
  {
    defaultExpanded: true
  }
)

const isExpanded = ref(props.defaultExpanded)

function getBadgeClasses(variant: string): string {
  return variant === 'dark'
    ? 'border-sucess bg-[#EAFBEF] text-textPrimary'
    : 'border-primary bg-[#D8EEF3] text-textPrimary'
}
</script>
